import { Navigate, useParams } from 'react-router-dom';
import { ButtonLink } from '@/components/ui/Button';
import { Reveal } from '@/components/ui/Reveal';
import { getCreatorContent } from '@/content/registry';
import { useDocumentTitle } from '@/lib/hooks';
import styles from './MoreAboutPage.module.css';

/**
 * The second layer of a profile: /:slug/how-i-show-up.
 *
 * Everything on this page comes from `profile.moreAbout`, so a second creator
 * gets their own version of it for free and nobody has to edit JSX to change a
 * sentence. A profile without that content has no teaser pointing here, so
 * arriving at this URL means someone typed it or followed a stale link — a
 * redirect is kinder than an empty page.
 *
 * `moreAbout.hidden` is treated exactly the same as no content at all. Nothing
 * on the site links here while it is set, and the URL is guessable, so the
 * check has to live on the route rather than only on the teaser.
 *
 * The two actions at the foot are ordered deliberately. The journey is still
 * the point of the site; this page is a detour that hands you back to it.
 */
export function MoreAboutPage() {
  const { slug = '' } = useParams();
  const content = getCreatorContent(slug);
  const profile = content?.profile;
  const moreAbout = profile?.moreAbout?.hidden ? undefined : profile?.moreAbout;

  useDocumentTitle(
    moreAbout && profile
      ? `${moreAbout.title} — ${profile.displayName} — Are We Compatible?`
      : 'Not found — Are We Compatible?',
  );

  if (!profile || !moreAbout) return <Navigate to="/404" replace />;

  return (
    <article className={styles.page}>
      <header className={styles.header}>
        <p className={styles.eyebrow}>{moreAbout.eyebrow}</p>
        <h1 className={styles.title}>{moreAbout.title}</h1>
        <p className={styles.intro}>{moreAbout.intro}</p>
      </header>

      <div className={styles.sections}>
        {moreAbout.sections.map((section, index) => (
          <section
            key={section.id}
            className={styles.section}
            aria-labelledby={`${section.id}-heading`}
          >
            <Reveal delay={index * 60}>
              <h2 id={`${section.id}-heading`} className={styles.sectionHeading}>
                {section.heading}
              </h2>
              <div className={styles.sectionBody}>
                {section.paragraphs.map((paragraph) => (
                  <p key={paragraph.slice(0, 32)}>{paragraph}</p>
                ))}
              </div>
            </Reveal>
          </section>
        ))}
      </div>

      <Reveal className={styles.closing}>
        <p>{moreAbout.closing}</p>
      </Reveal>

      <div className={styles.actions}>
        <ButtonLink to={`/${profile.slug}/questions`} size="large" withArrow>
          {moreAbout.actions.primary}
        </ButtonLink>
        <ButtonLink to={`/${profile.slug}`} variant="secondary" size="large">
          {moreAbout.actions.secondary}
        </ButtonLink>
      </div>
    </article>
  );
}
